import { Component } from '@theme/component';
import {
  debounce,
  lockScroll,
  onDocumentLoaded,
  setHeaderMenuStyle,
  unlockScroll,
} from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {boolean} */
  #apMegaScrollLocked = false;

  /** @type {number} */
  #apMegaScrollY = 0;

  /** @type {boolean} */
  #apMegaFixedBody = false;

  /** @type {ResizeObserver | null} */
  #apMegaHeaderResizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);

    // AP customization: click-only mega menu (ticket 38262)
    if (this.#isApMega()) {
      this.#layoutApMegaPanels();
    } else {
      this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    }

    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    this.addEventListener('pointerdown', this.#onApMegaItemPointerDown);
    document.addEventListener('theme-drawer:open', this.#onApMegaViewportChange);
    document.addEventListener('theme-drawer:close', this.#onApMegaViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    this.removeEventListener('pointerdown', this.#onApMegaItemPointerDown);
    document.removeEventListener('theme-drawer:open', this.#onApMegaViewportChange);
    document.removeEventListener('theme-drawer:close', this.#onApMegaViewportChange);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#unlockApMegaScroll();
  }

  /**
   * AP customization: merchants added matching mega panels (ticket 38262).
   * @returns {boolean}
   */
  #isApMega() {
    return this.classList.contains('ap-mega-menu');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getApOverlay() {
    const overlay = this.refs.apMegaOverlay ?? this.querySelector('.ap-mega-overlay');
    return overlay instanceof HTMLElement ? overlay : null;
  }

  /**
   * @param {string | null | undefined} value
   * @returns {string}
   */
  #normalizeApLabel(value) {
    return (value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /**
   * Match a nav item to a mega panel by handle, then title (ticket 38272).
   * Handles stay stable across locales; Translate & Adapt changes titles.
   * Empty panels (no nav children and no nested blocks) must not capture the click.
   * @param {Element} item
   * @returns {HTMLElement | null}
   */
  #findApPanel(item) {
    const overlay = this.#getApOverlay();
    if (!overlay) return null;

    const title = this.#normalizeApLabel(item.querySelector('.menu-list__link-title')?.textContent);
    const handle = this.#normalizeApLabel(item.getAttribute('data-handle'));
    const panels = overlay.querySelectorAll('.ap-mega-panel');

    let titleMatch = null;

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      if (!this.#apPanelHasContent(panel)) continue;

      const itemTitle = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-item'));
      const itemHandle = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-handle'));

      if (itemHandle && handle && itemHandle === handle) return panel;
      if (!titleMatch && itemTitle && itemTitle === title) titleMatch = panel;
    }

    return titleMatch;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  #apPanelHasContent(panel) {
    const flagged = panel.getAttribute('data-ap-mega-has-content');
    if (flagged === 'false') return false;
    if (flagged === 'true') return true;

    return Boolean(
      panel.querySelector('.ap-mega-group, .ap-mega-card, .ap-mega-featured-card, .ap-mega-link-group')
    );
  }

  /**
   * @param {HTMLElement} panel
   */
  #showApPanel(panel) {
    const overlay = this.#getApOverlay();
    if (!overlay) return;

    const panels = overlay.querySelectorAll('.ap-mega-panel');
    for (const candidate of panels) {
      if (!(candidate instanceof HTMLElement)) continue;
      candidate.classList.toggle('is-active', candidate === panel);
    }
  }

  /**
   * Featured-card `logo` is the live setting (nested `block.blocks` stubs are empty).
   * Clone it as a panel sibling before the featured column (Figma 208:11113).
   * @param {HTMLElement} panel
   */
  #placeApMegaLogo(panel) {
    const links = panel.querySelector('.ap-mega-panel__links');
    if (!links || panel.querySelector(':scope > .ap-mega-panel__logo')) return;

    const donor = panel.querySelector('.ap-mega-panel-logo-donor');
    if (!(donor instanceof HTMLElement)) return;

    const wrap = /** @type {HTMLElement} */ (donor.cloneNode(true));
    wrap.classList.remove('ap-mega-panel-logo-donor');
    wrap.classList.add('ap-mega-panel__logo');

    const featured = panel.querySelector('.ap-mega-panel__featured');
    if (featured) {
      panel.insertBefore(wrap, featured);
    } else {
      panel.append(wrap);
    }

    panel.classList.add('ap-mega-panel--has-logo');
  }

  /**
   * Move editor link-group blocks into the links column (ticket 38262).
   * @param {HTMLElement} panel
   */
  #layoutApMegaPanel(panel) {
    this.#placeApMegaLogo(panel);

    const featured = panel.querySelector('.ap-mega-panel__featured');
    const groupsSlot = panel.querySelector('.ap-mega-panel__editor-groups');
    if (!featured || !groupsSlot) return;

    const groups = featured.querySelectorAll('.ap-mega-group');
    for (const group of groups) {
      let node = group;
      while (node.parentElement && node.parentElement !== featured) {
        node = node.parentElement;
      }
      groupsSlot.append(node);
    }

    const shopifyGroups = panel.querySelector('.ap-mega-panel__links > .ap-mega-groups');
    if (shopifyGroups) {
      shopifyGroups.hidden = groupsSlot.childElementCount > 0;
    }
  }

  #layoutApMegaPanels() {
    const panels = this.querySelectorAll('.ap-mega-panel');
    for (const panel of panels) {
      if (panel instanceof HTMLElement) this.#layoutApMegaPanel(panel);
    }
    this.#markApMegaTriggers();
  }

  /**
   * Theme editor: show this Mega panel in the overlay (ticket 38262).
   * @param {HTMLElement} panel
   */
  previewApMegaPanel(panel) {
    if (!this.#isApMega() || !(panel instanceof HTMLElement)) return;

    this.#layoutApMegaPanel(panel);
    this.#showApPanel(panel);

    const overlay = this.#getApOverlay();
    if (!overlay) return;

    const item = this.#findMenuItemForPanel(panel);
    const previousItem = this.#state.activeItem;

    if (previousItem && previousItem !== item) {
      this.#expandableFor(previousItem).ariaExpanded = 'false';
    }

    if (item) {
      this.#state.activeItem = item;
      this.#expandableFor(item).ariaExpanded = 'true';
    }

    this.ariaExpanded = 'true';
    overlay.dataset.active = '';
    this.#setApOverlayInert(overlay, false);
    this.#lockApMegaScroll();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement | null}
   */
  #findMenuItemForPanel(panel) {
    const title = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-item'));
    const handle = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-handle'));
    const links = this.querySelectorAll('.menu-list__link[href][data-handle]');

    let titleMatch = null;

    for (const link of links) {
      if (!(link instanceof HTMLElement)) continue;

      const linkTitle = this.#normalizeApLabel(link.querySelector('.menu-list__link-title')?.textContent);
      const linkHandle = this.#normalizeApLabel(link.getAttribute('data-handle'));

      if (handle && linkHandle === handle) return link;
      if (!titleMatch && title && linkTitle === title) titleMatch = link;
    }

    return titleMatch;
  }

  /**
   * Liquid can only see Shopify nav children. Featured-only / cards panels
   * still need haspopup so iPad first-tap and screen readers match desktop.
   */
  #markApMegaTriggers() {
    const overlay = this.#getApOverlay();
    if (!overlay) return;

    const overlayId = overlay.id;
    const links = this.querySelectorAll('.menu-list__link[href][data-handle]');

    for (const link of links) {
      if (!(link instanceof HTMLElement)) continue;

      const panel = this.#findApPanel(link);
      if (panel) {
        link.setAttribute('aria-haspopup', 'true');
        if (!link.hasAttribute('aria-expanded')) {
          link.setAttribute('aria-expanded', 'false');
        }
        if (overlayId) link.setAttribute('aria-controls', overlayId);
        continue;
      }

      link.removeAttribute('aria-haspopup');
      link.removeAttribute('aria-controls');
      if (link.getAttribute('aria-expanded') === 'false') {
        link.removeAttribute('aria-expanded');
      }
    }
  }

  /**
   * AP customization: lock page scroll while the overlay is open (ticket 38262).
   * iOS Safari ignores `overflow: hidden` on `html`/`body` alone, so pin the body
   * on small viewports and block background `touchmove` (keep scrolling inside the panel).
   */
  #lockApMegaScroll() {
    if (this.#apMegaScrollLocked) return;

    lockScroll(this);
    this.#apMegaScrollLocked = true;

    document.body.classList.add('ap-mega-open');
    this.headerComponent?.setAttribute('data-ap-mega-open', '');
    document.getElementById('header-group')?.setAttribute('data-ap-mega-open', '');
    document.documentElement.classList.add('ap-mega-scroll-lock');

    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper instanceof HTMLElement) {
      pageWrapper.classList.add('ap-mega-scroll-lock');
    }

    const isDesktopSqueeze = window.matchMedia('(min-width: 990px)').matches;
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const scrollingElement = document.scrollingElement;
    this.#apMegaScrollY =
      isDesktopSqueeze && pageWrapper instanceof HTMLElement
        ? pageWrapper.scrollTop
        : scrollingElement instanceof HTMLElement
          ? scrollingElement.scrollTop
          : window.scrollY;

    // iOS/iPad ignore overflow:hidden on html/body (and often .page-wrapper).
    // Pin the body on coarse pointers (iPad desktop-width) and phones.
    if (isCoarsePointer || !isDesktopSqueeze) {
      this.#apMegaFixedBody = true;
      document.body.style.setProperty('position', 'fixed');
      document.body.style.setProperty('top', `-${this.#apMegaScrollY}px`);
      document.body.style.setProperty('left', '0');
      document.body.style.setProperty('right', '0');
      document.body.style.setProperty('width', '100%');
    }

    // Horizon `#header-component { contain: layout }` traps position:fixed (ticket 38262).
    this.headerComponent?.style.setProperty('contain', 'none');
    this.#applyApMegaHeaderColor();
    this.#positionApOverlay();
    this.#observeApMegaHeaderSize();

    document.addEventListener('touchmove', this.#onApMegaTouchMove, { passive: false });
    window.addEventListener('resize', this.#onApMegaViewportChange);
    window.visualViewport?.addEventListener('resize', this.#onApMegaViewportChange);
    window.visualViewport?.addEventListener('scroll', this.#onApMegaViewportChange);
  }

  /**
   * Follow Horizon’s cart squeeze so the overlay stays as wide as the header
   * (ticket 38262). `position: fixed` ignores `.page-wrapper` margin-right.
   */
  #observeApMegaHeaderSize() {
    this.#apMegaHeaderResizeObserver?.disconnect();

    if (!this.headerComponent) return;

    this.#apMegaHeaderResizeObserver = new ResizeObserver(() => {
      if (this.#apMegaScrollLocked) this.#positionApOverlay();
    });
    this.#apMegaHeaderResizeObserver.observe(this.headerComponent);
  }

  /**
   * Pin the overlay to the visible viewport (header bottom → screen bottom).
   * Use visualViewport width/left when the cart is closed — `100vw` includes
   * the scrollbar and gets clipped (tickets 38262 / 38260). When the cart
   * squeezes the page (≥990px), match the header box so mega and cart can
   * sit side by side.
   */
  #positionApOverlay() {
    if (!this.headerComponent) return;

    const overlay = this.#getApOverlay();
    const headerRect = this.headerComponent.getBoundingClientRect();
    const bottom = Math.round(headerRect.bottom);
    const viewport = window.visualViewport;
    const viewportHeight = viewport ? Math.round(viewport.height) : window.innerHeight;
    const viewportWidth = viewport ? Math.round(viewport.width) : window.innerWidth;
    const viewportLeft = viewport ? Math.round(viewport.offsetLeft) : 0;
    const squeezed =
      window.matchMedia('(min-width: 990px)').matches &&
      Boolean(document.querySelector('.page-wrapper--drawer-open'));

    this.headerComponent.style.setProperty('--ap-mega-overlay-top', `${bottom}px`);
    this.headerComponent.style.setProperty('--ap-mega-overlay-height', `${Math.max(0, viewportHeight - bottom)}px`);

    if (!(overlay instanceof HTMLElement)) return;

    overlay.style.right = 'auto';

    if (squeezed) {
      const left = Math.round(headerRect.left);
      const width = Math.round(headerRect.width);
      overlay.style.left = `${left}px`;
      overlay.style.width = `${width}px`;
      overlay.style.maxWidth = `${width}px`;
      return;
    }

    overlay.style.left = `${viewportLeft}px`;
    overlay.style.width = `${viewportWidth}px`;
    overlay.style.maxWidth = `${viewportWidth}px`;
  }

  /**
   * @param {TouchEvent} event
   */
  #onApMegaTouchMove = (event) => {
    const overlay = this.#getApOverlay();
    if (!overlay || event.touches.length > 1) return;

    const target = event.target;
    if (!(target instanceof Node) || !overlay.contains(target)) {
      event.preventDefault();
      return;
    }

    const inner = overlay.querySelector('.ap-mega-overlay__inner');
    if (inner instanceof HTMLElement && !inner.contains(target)) {
      event.preventDefault();
    }
  };

  #onApMegaViewportChange = () => {
    if (!this.#apMegaScrollLocked) return;
    this.#positionApOverlay();
  };

  #unlockApMegaScroll() {
    if (!this.#apMegaScrollLocked) return;

    document.removeEventListener('touchmove', this.#onApMegaTouchMove);
    window.removeEventListener('resize', this.#onApMegaViewportChange);
    window.visualViewport?.removeEventListener('resize', this.#onApMegaViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.#onApMegaViewportChange);
    this.#apMegaHeaderResizeObserver?.disconnect();
    this.#apMegaHeaderResizeObserver = null;

    document.body.classList.remove('ap-mega-open');
    this.headerComponent?.removeAttribute('data-ap-mega-open');
    document.getElementById('header-group')?.removeAttribute('data-ap-mega-open');
    document.documentElement.classList.remove('ap-mega-scroll-lock');
    document.querySelector('.page-wrapper')?.classList.remove('ap-mega-scroll-lock');

    if (this.#apMegaFixedBody) {
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');

      const pageWrapper = document.querySelector('.page-wrapper');
      const isDesktopSqueeze = window.matchMedia('(min-width: 990px)').matches;
      if (isDesktopSqueeze && pageWrapper instanceof HTMLElement) {
        pageWrapper.scrollTop = this.#apMegaScrollY;
      } else {
        window.scrollTo(0, this.#apMegaScrollY);
      }

      this.#apMegaFixedBody = false;
    }

    unlockScroll(this);
    this.#apMegaScrollLocked = false;
    this.headerComponent?.style.removeProperty('contain');
    this.headerComponent?.style.removeProperty('--ap-mega-overlay-top');
    this.headerComponent?.style.removeProperty('--ap-mega-overlay-height');

    const overlay = this.#getApOverlay();

    if (overlay) {
      overlay.style.removeProperty('left');
      overlay.style.removeProperty('right');
      overlay.style.removeProperty('width');
      overlay.style.removeProperty('max-width');
    }

    this.#clearApMegaHeaderColor();
  }

  /**
   * Copy the menu text color onto the header so localization and actions
   * match the open overlay (ticket 38262). `--ap-mega-header-color` lives on
   * this element and does not inherit up to `#header-component`.
   */
  #applyApMegaHeaderColor() {
    if (!this.headerComponent) return;

    const color = this.style.getPropertyValue('--ap-mega-header-color').trim();
    const rgb = this.style.getPropertyValue('--ap-mega-header-color-rgb').trim();
    if (!color) return;

    this.headerComponent.style.setProperty('--ap-mega-header-color', color);
    if (rgb) this.headerComponent.style.setProperty('--ap-mega-header-color-rgb', rgb);
  }

  #clearApMegaHeaderColor() {
    this.headerComponent?.style.removeProperty('--ap-mega-header-color');
    this.headerComponent?.style.removeProperty('--ap-mega-header-color-rgb');
  }

  /**
   * Unlock immediately on close so homepage slideshows keep working (ticket 38262).
   * Waiting on clip-path / getAnimations left touchmove + overflow:hidden stuck.
   * @param {HTMLElement} overlay
   */
  #finishApMegaClose(overlay) {
    this.#setApOverlayInert(overlay, true);
    this.#unlockApMegaScroll();
  }

  /**
   * `element.inert = false` can leave the `inert` attribute, which breaks Horizon
   * `:has(.menu-list__submenu:not([inert]))` stacking (ticket 38262).
   * @param {HTMLElement | null} overlay
   * @param {boolean} inert
   */
  #setApOverlayInert(overlay, inert) {
    if (!overlay) return;

    overlay.inert = inert;

    if (inert) {
      overlay.setAttribute('inert', '');
    } else {
      overlay.removeAttribute('inert');
    }
  }

  /**
   * AP customization: close the overlay (Sluiten is not inside a list item) (ticket 38262).
   * @param {Event} [event]
   */
  closeApMega = (event) => {
    event?.preventDefault();
    if (this.#state.activeItem) {
      this.#deactivate(this.#state.activeItem, { force: true });
    }
  };

  /**
   * AP customization: close the overlay on outside click (ticket 38262).
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#isApMega()) return;
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    if (event.target instanceof Element && event.target.closest('#cart-drawer, [aria-controls="cart-drawer"]')) {
      return;
    }

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * iPad/iOS treat a tapped `<a aria-haspopup>` as hover-first; open on pointerdown
   * so the first tap opens the overlay (ticket 38262).
   * @param {PointerEvent} event
   */
  #onApMegaItemPointerDown = (event) => {
    if (!this.#isApMega()) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.ap-mega-close, .ap-mega-overlay')) return;

    const isCoarse = window.matchMedia('(pointer: coarse)').matches;
    if (!isCoarse && event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

    const item = findMenuItem(event.target);
    if (!item || !this.#findApPanel(item)) return;

    event.preventDefault();
    this.toggle(event);
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    if (this.#apMegaScrollLocked) this.#positionApOverlay();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close the open submenu on Escape and restore focus to its disclosure control.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  /**
   * Toggle a submenu from its dedicated disclosure button (click / Enter / Space).
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    // AP customization: top-level items are still <a href> — keep the click on-page
    // when a matching mega panel has content. Empty / unmatched links navigate (ticket 38262).
    if (this.#isApMega()) {
      const panel = this.#findApPanel(item);
      if (!panel) {
        if (this.#state.activeItem) {
          this.#deactivate(this.#state.activeItem, { force: true });
        }

        return;
      }

      event.preventDefault();
    }

    const isExpanded = this.#expandableFor(item).getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      // Close whatever is currently active. In the overflow panel the initially
      // selected item is tracked as `activeOverflowItem` while the More trigger is
      // the `activeItem`, so deactivating `item` directly would no-op; deactivate
      // the active item instead.
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      // Closing the overflow panel hides the focused disclosure, so return focus
      // to the visible More trigger; a top-level disclosure stays put.
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      this.activate(event);
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  /**
   * Return the element that carries the `aria-expanded` state for a menu item.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    if (this.#isApMega() && !this.#findApPanel(item)) return;

    // Derive the slot context from the list item rather than `event.target`.
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';
      if (!this.#isApMega()) {
        const previousSubmenu = findSubmenu(previouslyActiveItem);
        if (previousSubmenu) previousSubmenu.inert = true;
      }
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      this.#expandableFor(previouslyActiveOverflowItem).ariaExpanded = 'false';
      const previousOverflowSubmenu = findSubmenu(previouslyActiveOverflowItem);
      if (previousOverflowSubmenu) previousOverflowSubmenu.inert = true;
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = 'true';
      // The More trigger auto-opens the first overflow item's submenu, so expose it.
      const activeOverflowSubmenu = findSubmenu(overflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = false;
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);

    if (this.#isApMega()) {
      const panel = this.#findApPanel(item);
      if (!panel) return;

      this.#layoutApMegaPanel(panel);
      this.#showApPanel(panel);
      submenu = this.#getApOverlay();
    }

    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      if (this.#isApMega()) {
        this.#setApOverlayInert(submenu, false);
        this.#lockApMegaScroll();
      } else {
        submenu.inert = false;
      }

      // AP customization: full-viewport overlay — do not size Horizon's underlay
      // from overlay offsetHeight (ticket 38262).
      if (this.#isApMega()) {
        this.#cleanupMutationObserver();
        this.headerComponent?.style.setProperty('--submenu-height', '0px');
        this.#setFullOpenHeaderHeight(0, 0);
        this.style.setProperty('--submenu-opacity', '1');
        return;
      }

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');

    // AP customization: no hover safety-box tracking when click-only (ticket 38262)
    if (!this.#isApMega()) {
      this.#startPointerTracking(item, previouslyActiveItem);
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    // Keep the submenu open while focus moves between the link, its disclosure
    // button, and the submenu contents — i.e. anywhere inside the same list item.
    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle),
    // which must close regardless of pointer position.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = this.#isApMega()
      ? this.#getApOverlay()
      : findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'false';
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = true;
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      if (this.#isApMega()) {
        this.#finishApMegaClose(submenu);
      } else {
        submenu.inert = true;
      }
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // `element` may be the list item itself (hover/focus routed via the `<li>`) or a
  // descendant such as the disclosure button (click routed via the button).
  const listItem = element.closest('.menu-list__list-item') ?? element;
  return listItem.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
